"use client";

import Link from "next/link";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useChecklist } from "@/hooks/useChecklist";
import {
  applicationChecklistItems,
  APPLICATION_CHECKLIST_COOKIE,
} from "@/lib/application-checklist";

/**
 * 좁은 좌측 사이드바용 원서 접수 체크리스트 (JCG-2101).
 *
 * jeongcheogi-application 본문 표(ChecklistTable)와 **동일 항목·쿠키를 공유**하며, useChecklist 의
 * 커스텀 이벤트 동기화로 본문↔사이드바 체크가 양방향 즉시 반영된다. 표(4열) 대신 항목당 체크박스 +
 * 할 일 + 일정 + 방법 링크를 세로로 쌓아 좁은 폭(약 320~372px)에 맞춘다. 색상은 디자인 토큰만 사용해
 * 다크모드 자동 대응.
 *
 * 셸 좌측에서는 체크리스트 ↕ 목차 세로 스플릿(지도↕목차와 동일) pane 안에 들어가므로, 루트가 부모
 * 높이를 채우고(`flex min-h-0 flex-1`) 리스트만 내부 스크롤한다 — 진행 표시는 상단 고정(shrink-0).
 */
export default function ChecklistMini() {
  const { checkedState, toggle } = useChecklist(APPLICATION_CHECKLIST_COOKIE);
  const total = applicationChecklistItems.length;
  const done = applicationChecklistItems.reduce(
    (n, item) => (checkedState[item.id] ? n + 1 : n),
    0,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5">
      <p
        aria-live="polite"
        className="shrink-0 text-[11px] font-bold text-muted-foreground"
      >
        완료 {done}/{total}
      </p>
      <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-0.5">
        {applicationChecklistItems.map((item) => {
          const checked = checkedState[item.id] || false;
          return (
            <li
              key={item.id}
              className="flex gap-2 rounded-lg border border-border bg-card px-2 py-1.5"
            >
              <Checkbox
                aria-label={`${item.task} 완료`}
                checked={checked}
                onCheckedChange={() => toggle(item.id)}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "text-[12.5px] font-bold leading-snug text-foreground",
                    checked && "text-muted-foreground line-through",
                  )}
                >
                  {item.task}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  {item.schedule}
                </p>
                <Link
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 inline-block text-[11px] font-semibold text-amber-600 hover:underline dark:text-amber-400"
                >
                  {item.method}
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
